type SanitizationConfig = {
  removeClasses: string[];
  removeImagePatterns: RegExp[];
  styleReplacements: Array<{ pattern: RegExp; replacement: string }>;
};

const DEFAULT_CONFIG: SanitizationConfig = {
  removeClasses: ['start', 'disclaimer', 'faclitity-productions', 'facility-productions', 'end'],
  removeImagePatterns: [/^<img.*(triangle|footer|pdf-logo).*" \/>$/gm],
  styleReplacements: [
    {
      pattern:
        /\.container{padding-right:15px;padding-left:15px;margin-right:auto;margin-left:auto}/gm,
      replacement:
        '.container{padding-right:15px;padding-left:15px;margin-right:auto;margin-left:auto;width:100%}',
    },
    {
      pattern: /#content{width:1280px !important;height:720px !important;/gm,
      replacement: '#content{',
    },
    {
      pattern:
        /@media \(min-width: 768px\){\.container{width:750px}}@media \(min-width: 992px\){\.container{width:970px}}@media \(min-width: 1200px\){\.container{width:1170px}}/gm,
      replacement: '',
    },
    {
      pattern: /\.map-image{width:590px;height:519px;/gm,
      replacement: '.map-image{',
    },
    {
      pattern:
        /\.start-cover{text-align:right;padding-top:350px;padding-left:200px;padding-right:210px;/gm,
      replacement: '.start-cover{text-align:right;',
    },
    {
      pattern: /\.sold-asset{margin-left:763px;margin-right:210px}/gm,
      replacement: '.sold-asset{text-align:right;}',
    },
    {
      pattern: /\.end{text-align:right;width:1280px !important;/gm,
      replacement: '.end{text-align:right;',
    },
    {
      pattern: /html{font-size:10px;/gm,
      replacement: 'html{',
    },
  ],
};

const applyStyleReplacements = (html: string, config: SanitizationConfig): string => {
  return config.styleReplacements.reduce((result, { pattern, replacement }) => {
    return result.replace(pattern, replacement);
  }, html);
};

const removeImagePatterns = (html: string, config: SanitizationConfig): string => {
  return config.removeImagePatterns.reduce((result, pattern) => {
    return result.replace(pattern, '');
  }, html);
};

const removePageNumbers = (html: string): string => {
  return html.replace(/^<div class='page-number.*\/div>$/gm, '');
};

const removeUnwantedElements = (body: HTMLBodyElement, config: SanitizationConfig): void => {
  config.removeClasses.forEach(className => {
    const elements = Array.from(body.getElementsByClassName(className));
    for (const element of elements) {
      element.remove();
    }
  });
};

const preprocessAssetReportHtml = (
  html: string,
  config: SanitizationConfig = DEFAULT_CONFIG,
): string => {
  let processedHtml = html;

  processedHtml = removeImagePatterns(processedHtml, config);
  processedHtml = removePageNumbers(processedHtml);
  processedHtml = applyStyleReplacements(processedHtml, config);

  return processedHtml;
};

const finalizeAssetReportHtml = (
  processedHtml: string,
  config: SanitizationConfig = DEFAULT_CONFIG,
): string => {
  if (typeof document === 'undefined') {
    return processedHtml;
  }

  const wrapper = document.createElement('html');
  wrapper.innerHTML = processedHtml;

  const body = wrapper.getElementsByTagName('body')[0];
  if (body) {
    removeUnwantedElements(body, config);
  }

  return wrapper.outerHTML;
};

const sanitizeAssetReportHtml = (
  html: string,
  config: SanitizationConfig = DEFAULT_CONFIG,
): string => {
  const processedHtml = preprocessAssetReportHtml(html, config);
  return finalizeAssetReportHtml(processedHtml, config);
};

const removeElementsById = (ids: string[]): void => {
  if (typeof document === 'undefined') {
    return;
  }

  ids.forEach(id => {
    let element = document.getElementById(id);
    while (element) {
      element.remove();
      element = document.getElementById(id);
    }
  });
};

export {
  sanitizeAssetReportHtml,
  finalizeAssetReportHtml,
  preprocessAssetReportHtml,
  removeElementsById,
  DEFAULT_CONFIG,
};
export type { SanitizationConfig };
